import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FreudMessageService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import { SpeechToTextService } from 'app/services/speech-to-text.service';
import { UserService } from 'app/services/user.service';

@Component({
	selector: 'speech-to-text',
	templateUrl: './speech-to-text.component.html',
	styleUrls: ['./speech-to-text.component.scss'],
})
export class SpeechToTextComponent implements OnInit {
	initButtonDisabled = false;
	stopButtonDisabled = false;
	initButtonLabel = '';
	stopButtonLabel = '';
	isPremium = false;
	private mediaRecorder: MediaRecorder;
	private audioChunks: BlobPart[] = [];
	private isRecording = false;
	private websocketInterval: any;
	private callCount = 0;

	@Output() recording: EventEmitter<boolean> = new EventEmitter<boolean>();

	constructor(
		private speechToTextService: SpeechToTextService,
		protected translateService: TranslateService,
		private user: UserService,
		private toast: FreudMessageService
	) {}

	ngOnInit(): void {
		this.user
			.checkSubscriptionAndContactTiers()
			.then(({ hasActiveSubscription }) => {
				if (hasActiveSubscription) {
					this.buttonsStateWhenStart();
					this.isPremium = true;
					return;
				}
				this.isPremium = false;
			});
	}

	connectToSpeechToText() {
		this.speechToTextService.connectWebSocket();
		this.listenSpeechToText();
		this.scheduleWebSocketCalls();
	}

	scheduleWebSocketCalls() {
		// The first two calls are made every 5 seconds because the API bill for at least 10 seconds of audio
		this.websocketInterval = setInterval(() => {
			this.callCount++;
			if (this.callCount <= 2) {
				this.sendSpeechToText(
					new Blob(this.audioChunks, { type: 'audio/mpeg' })
				);
				if (this.callCount === 2) {
					clearInterval(this.websocketInterval);
					this.websocketInterval = setInterval(() => {
						this.sendSpeechToText(
							new Blob(this.audioChunks, { type: 'audio/mpeg' })
						);
					}, 2500);
				}
			}
		}, 5000);
	}

	openMicrophone() {
		this.connectToSpeechToText();

		navigator.mediaDevices
			.getUserMedia({ audio: true })
			.then((stream) => {
				this.mediaRecorder = new MediaRecorder(stream);

				this.mediaRecorder.ondataavailable = (event) => {
					if (event.data.size > 0) {
						this.audioChunks.push(event.data);
					}
				};

				this.mediaRecorder.onstop = () => {
					this.isRecording = false;
					this.sendFinalAudio();
					this.resetMediaRecorder();
				};

				// updates the recording every 500ms
				this.mediaRecorder.start(500);
			})
			.catch((error) => {
				console.error('Error accessing microphone:', error);
				this.stop();
			});
	}

	startRecording(): void {
		// Show the user that the recording has started, then it will be replaced by the actual text
		this.speechToTextService.setText('...');
		this.audioChunks = [];
		this.openMicrophone();
		this.isRecording = true;
		this.recording.emit(true);
	}

	resetMediaRecorder() {
		if (this.mediaRecorder) {
			this.mediaRecorder.ondataavailable = null;
			this.mediaRecorder.onstop = null;
			this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
			this.mediaRecorder = null;
		}
	}

	stopRecording(): void {
		if (this.mediaRecorder && this.isRecording) {
			this.mediaRecorder.stop();
		}
	}

	sendFinalAudio() {
		// After the recording is stopped, the final audio chunk is sent to the API
		if (this.audioChunks.length > 0) {
			const finalBlob = new Blob(this.audioChunks, { type: 'audio/mpeg' });
			this.sendSpeechToText(finalBlob);
		}
		clearInterval(this.websocketInterval);
		this.callCount = 0;
	}

	listenSpeechToText() {
		this.speechToTextService.listen().subscribe(
			(message: string) => {
				this.speechToTextService.setText(message.trim());
			},
			(error: Error) => {
				console.error('WebSocket error:', error);
				this.toast.add({
					severity: 'error',
					detail: this.translateService.instant(
						'PROMPTUARY.SPEECH_TO_TEXT.ERROR_CONNECT_WEBSOCKET.DESCRIPTION'
					),
					summary: this.translateService.instant(
						'PROMPTUARY.SPEECH_TO_TEXT.ERROR_CONNECT_WEBSOCKET.TITLE'
					),
					life: 5000,
				});
				this.stop();
			}
		);
	}

	sendSpeechToText(data: Blob) {
		this.speechToTextService.sendBinary(data);
	}

	disconnectSpeechToText() {
		this.speechToTextService.disconnectWebSocket();
	}

	buttonsStateWhenStart() {
		this.initButtonLabel = this.translateService.instant(
			'PROMPTUARY.SPEECH_TO_TEXT.START_BUTTON'
		);
		this.stopButtonLabel = this.translateService.instant(
			'PROMPTUARY.SPEECH_TO_TEXT.STOP_BUTTON'
		);
		this.initButtonDisabled = false;
		this.stopButtonDisabled = true;
	}

	ngOnDestroy(): void {
		if (this.mediaRecorder && this.isRecording) {
			this.stopRecording();
		}
		this.disconnectSpeechToText();
		clearInterval(this.websocketInterval);
	}

	init() {
		this.initButtonLabel = this.translateService.instant(
			'PROMPTUARY.SPEECH_TO_TEXT.LISTENING'
		);
		this.initButtonDisabled = true;
		this.stopButtonDisabled = false;
		this.startRecording();
	}

	stop() {
		this.stopButtonLabel = this.translateService.instant(
			'PROMPTUARY.SPEECH_TO_TEXT.STOPPING'
		);
		this.initButtonLabel = this.translateService.instant(
			'PROMPTUARY.SPEECH_TO_TEXT.START_BUTTON'
		);
		this.initButtonDisabled = true;
		this.stopButtonDisabled = true;

		this.stopRecording();
		this.buttonsStateWhenStart();

		// Wait to disconnect the WebSocket to avoid losing the last transcription
		setTimeout(() => {
			this.disconnectSpeechToText();
			this.recording.emit(false);
		}, 1500);
	}
}
